import { useState } from "react";
import { X } from "lucide-react";
import { Button, Screen, Text } from "@ferrivia/ui";
import { useQuestionnaireCopy } from "@ferrivia/questionnaire-ui";
import { useTranslation } from "@ferrivia/i18n";
import s from "./ConsentGate.module.css";

export interface ConsentGateProps {
  onAccept(): Promise<void>;
  onClose(): void;
}

/** Screen 1 of the deck: the just-in-time consent screen shown before a
    client's first keystroke into the questionnaire. One checkbox, matching
    the shipped copy and the `start` route's single `consent.granted` boolean
    (see the plan's "Two unbundled consent checkboxes vs. one" — a second,
    Sydänkeskus-specific checkbox is a GDPR call for Ville, not built here). */
export function ConsentGate({ onAccept, onClose }: ConsentGateProps) {
  const { t } = useTranslation("client");
  const copy = useQuestionnaireCopy();
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [failed, setFailed] = useState(false);

  async function handleAccept() {
    if (!checked || submitting) return;
    setSubmitting(true);
    setFailed(false);
    try {
      await onAccept();
    } catch {
      setFailed(true);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Screen className={s.root}>
      <div className={s.scroll}>
        <div className={s.header}>
          <button
            type="button"
            className={s.close}
            onClick={onClose}
            aria-label={t("questionnaire.close", "Sulje")}
          >
            <X aria-hidden size={20} />
          </button>
        </div>

        <Text as="h1" size="2xl" weight="bold" className={s.title}>
          {copy.consent.title}
        </Text>

        {copy.consent.intro.map((paragraph, index) => (
          <Text as="p" size="md" className={s.paragraph} key={index}>
            {paragraph}
          </Text>
        ))}

        <Text as="p" size="md" weight="medium" className={s.emphasis}>
          {copy.consent.emphasis}
        </Text>

        <Text as="p" size="md" className={s.paragraph}>
          {copy.consent.outro}
        </Text>

        <label className={s.consentRow}>
          <input
            type="checkbox"
            className={s.checkbox}
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          <span className={s.consentLabel}>{copy.consent.consentStatement}</span>
        </label>

        {failed ? (
          <Text as="p" size="sm" className={s.error} role="alert">
            {t("questionnaire.consentFailed", "Hyväksyntää ei voitu tallentaa. Yritä uudelleen.")}
          </Text>
        ) : null}
      </div>

      <div className={s.footer}>
        <Button className={s.accept} disabled={!checked || submitting} onClick={() => void handleAccept()}>
          {copy.consent.accept}
        </Button>
      </div>
    </Screen>
  );
}
